// useGoogleAnalytics.js
// React hook to track page views in a SPA with Google Analytics 4

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Get Google Analytics Measurement ID
 * Priority: Environment variable > Extract from existing script tag
 * Format: G-XXXXXXXXXX
 */
const getMeasurementID = () => {
  // First, try environment variable
  if (process.env.REACT_APP_GA_MEASUREMENT_ID) {
    return process.env.REACT_APP_GA_MEASUREMENT_ID;
  }

  // Otherwise, try to extract from the script tag in the HTML
  if (typeof document !== 'undefined') {
    const script = document.querySelector('script[src*="googletagmanager.com/gtag/js"]');
    if (script && script.src) {
      const match = script.src.match(/id=([^&]+)/);
      if (match && match[1]) {
        return match[1];
      }
    }
  }

  return null;
};

const GA_MEASUREMENT_ID = getMeasurementID();

/**
 * Track a page view
 * @param {string} path - The path to track
 */
export const trackPageView = (path) => {
  if (!GA_MEASUREMENT_ID) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Google Analytics: Measurement ID not found. Analytics disabled.');
    }
    return;
  }

  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_path: path,
    });
  }
};

/**
 * Track a custom event
 * @param {string} eventName - Name of the event
 * @param {object} eventParams - Additional parameters for the event
 */
export const trackEvent = (eventName, eventParams = {}) => {
  if (!GA_MEASUREMENT_ID) return;

  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
};

/**
 * React hook to automatically track page views on route changes
 * Use this in your main App component or Layout component
 */
export const useGoogleAnalytics = () => {
  const location = useLocation();

  useEffect(() => {
    // Track page view when route changes
    trackPageView(location.pathname + location.search);
  }, [location]);
};

